import React from 'react';
import MarketplacePracticeLocation from '../MarketplacePracticeLocation/MarketplacePracticeLocation';
import { EntityLocationInfo } from '../../../../slices/profiles-data-slice/models/entity-location-info';
import './MarketplacePracticeLocationsTable.scss';
import { useFormikContext, getIn } from 'formik';
import { useServiceFocuses } from '../../../../custom-hooks/useServiceFocuses';
import { isOtherFocus } from '../../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import { locationTypeTooltipHtml } from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';

interface MarketplacePracticeLocationsTableProps {
    locations: EntityLocationInfo[];
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    removeLocation(locationIndex: number): void;
    isEditable?: boolean;
    canEditRatings?: boolean;
    timelineId?: number;
    practiceId?: string;
}

export const MarketplacePracticeLocationsTable: React.FC<
    MarketplacePracticeLocationsTableProps
> = (props: MarketplacePracticeLocationsTableProps) => {
    const { isEditable = true } = props;
    const { values } = useFormikContext();
    const serviceFocuses = useServiceFocuses();

    const isOtherPracticeFocusInputVisible = () => {
        const locations = getIn(
            values,
            `${props.fieldPrefix}`
        ) as EntityLocationInfo[];

        return locations?.some((location) =>
            isOtherFocus(serviceFocuses, location.practiceFocusType)
        );
    };

    const showReadonlyRatingColumns = () => {
        if (props.canEditRatings) {
            return false;
        }
        const locations = getIn(
            values,
            `${props.fieldPrefix}`
        ) as EntityLocationInfo[];
        return !!locations?.some(
            (location) =>
                location.ratings?.google != null ||
                location.ratings?.facebook != null ||
                location.ratings?.yelp != null
        );
    };

    return (
        <div className="practice-locations-table">
            <table className="table locations-table">
                <thead>
                    <tr>
                        <th>DBA Name</th>
                        <th>Street Address</th>
                        <th>City</th>
                        <th>State</th>
                        <th style={{ width: '90px' }}>Zip</th>
                        <th>
                            Location Type
                            <InfoTooltip
                                idText="location-type-table-tooltip"
                                text=""
                                html={locationTypeTooltipHtml}
                                place="top"
                            />
                        </th>
                        <th style={{ width: '100px' }}>FTE DVMs</th>
                        <th>Service Focus</th>
                        <th hidden={!isOtherPracticeFocusInputVisible()}>
                            Specify Service Focus
                        </th>
                        {props.canEditRatings && (
                            <>
                                <th className="location-rating-header">
                                    Google rating
                                </th>
                                <th className="location-rating-header">
                                    Google # of reviews
                                </th>
                                <th className="location-rating-header">
                                    Facebook rating
                                </th>
                                <th className="location-rating-header">
                                    Facebook # of reviews
                                </th>
                                <th className="location-rating-header">
                                    Yelp rating
                                </th>
                                <th className="location-rating-header">
                                    Yelp # of reviews
                                </th>
                            </>
                        )}
                        {showReadonlyRatingColumns() && (
                            <>
                                <th className="location-rating-header">
                                    Google rating
                                </th>
                                <th className="location-rating-header">
                                    Facebook rating
                                </th>
                                <th className="location-rating-header">
                                    Yelp rating
                                </th>
                            </>
                        )}
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {props.locations.map((location, index) => (
                        <MarketplacePracticeLocation
                            key={location.id}
                            location={location}
                            index={index}
                            fieldPrefix={props.fieldPrefix}
                            setFormFieldValue={props.setFormFieldValue}
                            removeLocation={props.removeLocation}
                            isEditable={isEditable}
                            canEditRatings={props.canEditRatings}
                            showReadonlyRatings={showReadonlyRatingColumns()}
                            isOtherPracticeFocusColumnVisible={isOtherPracticeFocusInputVisible()}
                            timelineId={props.timelineId}
                            practiceId={props.practiceId}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default MarketplacePracticeLocationsTable;
